import { useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { cn } from '@/lib/utils'

// Transparent real <select> that covers its (relatively positioned) parent,
// so iOS presents its native picker and the web gets the browser's own
// dropdown while the visible control stays fully styled (per design: "when
// there is a pop up select use native select menu"). Used by PrntrSelect
// rows and the profile-card stats period pill.
export function NativeSelectOverlay<V extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  className,
}: {
  value: V | null
  onChange: (next: V) => void
  options: readonly { value: V; label: string }[]
  ariaLabel?: string
  className?: string
}) {
  // Bumped after every iOS selection to remount the <select>. WebKit restores
  // focus to a select after its native menu dismisses (even past blur()), and
  // the still-focused select swallows the next tap anywhere to re-open itself
  // instead of letting the tapped row act. Destroying the element is the only
  // reliable way to drop that focus state.
  const [pickerEpoch, setPickerEpoch] = useState(0)
  return (
    <select
      key={pickerEpoch}
      className={cn(
        'absolute inset-0 size-full cursor-pointer appearance-none text-base opacity-0',
        className,
      )}
      value={value ?? ''}
      onChange={(e) => {
        onChange(e.target.value as V)
        // Drop focus once a value is picked so the closed picker can't
        // re-trigger from residual focus. On iOS blur() alone isn't
        // enough — WebKit re-focuses after the menu dismisses — so
        // remount the element instead (see pickerEpoch above).
        e.currentTarget.blur()
        if (Capacitor.getPlatform() === 'ios') setPickerEpoch((n) => n + 1)
      }}
      onFocus={(e) => {
        // iOS never blurs a transparent select when its menu is dismissed
        // without a selection (tap-away, or re-tapping the current value —
        // no change event either way); the still-focused select then
        // swallows the next tap anywhere and re-opens. While focused,
        // intercept the first pointer-down that isn't on this select
        // (capture phase, before the tap completes): blur() for the web,
        // and on iOS also disable + remount the element — WebKit keeps
        // redirecting taps to a merely-blurred select, but a disabled or
        // destroyed one it has to let go of, so the tap reaches its real
        // target.
        const el = e.currentTarget
        const cleanup = () => {
          document.removeEventListener('pointerdown', onPointerDown, true)
          document.removeEventListener('touchstart', onPointerDown, true)
        }
        const onPointerDown = (ev: Event) => {
          if (ev.target === el) return
          el.blur()
          if (Capacitor.getPlatform() === 'ios') {
            el.disabled = true
            cleanup()
            setPickerEpoch((n) => n + 1)
          }
        }
        document.addEventListener('pointerdown', onPointerDown, true)
        document.addEventListener('touchstart', onPointerDown, true)
        el.addEventListener('blur', cleanup, { once: true })
      }}
      aria-label={ariaLabel}
    >
      {value === null && <option value="" disabled hidden />}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}
